import {
  Col,
  Flex,
  Form,
  Input,
  Paragraph,
  Row,
  Switch,
  Textarea,
  type FormInstance,
} from "@ir/ui";
import { ErrorBoundary } from "@sentry/react";
import { ImageCrop } from "@src/presentation/components/common/imagecrop";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ORGANIZATION_DISPLAY_NAME_MAX_LENGTH,
  ORGANIZATION_KEY_MAX_LENGTH,
  ORGANIZATION_NAME_MAX_LENGTH,
} from "@src/domain/dto/organizations";
import {
  createDuplicateKeyValidator,
  DESCRIPTION_MAX_LENGTH_RULES,
  generateEntityKey,
  getInputValue,
  isAddEditFormDirty,
  LOGO_ACCEPT,
  LOGO_FILE_VALIDATION_RULES,
  sanitizeKeyInput,
} from "./addEditFormUtils";
import { ORGANIZATION_KEY_ALREADY_EXISTS_MESSAGE } from "./organizationKeyValidation";
import { SavedLogoPreview } from "./SavedLogoPreview";

const ORGANIZATION_KEY_CHARS = /[^A-Z-]/g;

function toDirtyComparable(values: OrganizationFormValues) {
  return {
    name: values.organizationName,
    displayName: values.organizationDisplayName,
    description: values.organizationDescription,
    isActive: values.organizationIsActive,
    logoFile: values.organizationLogoFile,
    removeLogo: values.removeOrganizationLogo,
  };
}

export interface OrganizationFormValues {
  organizationId?: number | null;
  organizationKey: string;
  organizationName: string;
  organizationDisplayName: string;
  organizationDescription?: string | null;
  organizationLogoFile?: File | null;
  /** Edit: mark saved logo for removal on save (multipart empty logo_* parts). */
  removeOrganizationLogo?: boolean;
  organizationIsActive: boolean;
}

interface OrganizationsAddEditFormProps {
  form: FormInstance<OrganizationFormValues>;
  mode: "create" | "edit";
  initialValues: OrganizationFormValues;
  /** Edit: show saved logo when the URL is known (not replaced by a new file yet). */
  existingLogoUrl?: string | null;
  /** Create: keys from loaded/cached organizations for pre-submit duplicate check. */
  existingOrganizationKeys?: ReadonlySet<string>;
  /** Edit: skip the current organization's key when checking duplicates. */
  excludeOrganizationKey?: string;
  onSubmit: (values: OrganizationFormValues) => Promise<void> | void;
  onFormValidityChange?: (isSubmittable: boolean) => void;
}

export const OrganizationsAddEditForm = ({
  form,
  mode,
  initialValues,
  existingLogoUrl,
  existingOrganizationKeys,
  excludeOrganizationKey,
  onSubmit,
  onFormValidityChange,
}: OrganizationsAddEditFormProps) => {
  const [isKeyEditedManually, setIsKeyEditedManually] = useState(false);
  const [showSavedLogoPreview, setShowSavedLogoPreview] = useState(true);

  const updateFormValidity = useCallback(() => {
    const hasErrors = form
      .getFieldsError()
      .some((field) => (field.errors?.length ?? 0) > 0);
    const values = form.getFieldsValue();
    const hasRequiredValues =
      (values.organizationName?.trim() ?? "") !== "" &&
      (values.organizationDisplayName?.trim() ?? "") !== "" &&
      (values.organizationKey?.trim() ?? "") !== "";
    const isDirty =
      mode === "create" ||
      isAddEditFormDirty(toDirtyComparable(values), toDirtyComparable(initialValues));
    onFormValidityChange?.(hasRequiredValues && !hasErrors && isDirty);
  }, [form, initialValues, mode, onFormValidityChange]);

  useEffect(() => {
    form.setFieldsValue(initialValues);
    setIsKeyEditedManually(
      mode === "edit" ? true : initialValues.organizationKey !== "",
    );
    setShowSavedLogoPreview(true);
    updateFormValidity();
  }, [form, initialValues, mode, updateFormValidity]);

  const organizationKeyDisabled = useMemo(() => mode === "edit", [mode]);

  const organizationNameRules = useMemo(
    () => [
      { required: true, message: "Legal name is required" },
      {
        max: ORGANIZATION_NAME_MAX_LENGTH,
        message: `Legal name must be less than or equal to ${ORGANIZATION_NAME_MAX_LENGTH} characters.`,
      },
    ],
    [],
  );

  const organizationDisplayNameRules = useMemo(
    () => [
      { required: true, message: "Display name is required" },
      {
        max: ORGANIZATION_DISPLAY_NAME_MAX_LENGTH,
        message: `Display name must be less than or equal to ${ORGANIZATION_DISPLAY_NAME_MAX_LENGTH} characters.`,
      },
    ],
    [],
  );

  const organizationKeyRules = useMemo(
    () => [
      { required: true, message: "Organization key is required" },
      {
        pattern: /^[A-Z]+(-[A-Z]+)*$/,
        message:
          "Only uppercase letters and single hyphens between words are allowed",
      },
      {
        max: ORGANIZATION_KEY_MAX_LENGTH,
        message: `Organization key must be less than or equal to ${ORGANIZATION_KEY_MAX_LENGTH} characters.`,
      },
      {
        validator: createDuplicateKeyValidator(
          existingOrganizationKeys,
          excludeOrganizationKey,
          ORGANIZATION_KEY_ALREADY_EXISTS_MESSAGE,
        ),
      },
    ],
    [existingOrganizationKeys, excludeOrganizationKey],
  );

  useEffect(() => {
    const key = form.getFieldValue("organizationKey");
    if (!key?.trim()) return;
    form.validateFields(["organizationKey"]).catch(() => {});
  }, [existingOrganizationKeys, excludeOrganizationKey, form]);

  const syncOrganizationKeyFromLegalName = (valueOrEvent: unknown) => {
    if (organizationKeyDisabled || isKeyEditedManually) return;
    form.setFieldsValue({
      organizationKey: generateEntityKey(
        getInputValue(valueOrEvent),
        ORGANIZATION_KEY_CHARS,
        ORGANIZATION_KEY_MAX_LENGTH,
      ),
    });
    form.validateFields(["organizationKey"]).catch(() => {});
  };

  const handleOrganizationNameChange = (valueOrEvent: unknown) => {
    form.setFieldsValue({
      organizationName: getInputValue(valueOrEvent),
    });
    syncOrganizationKeyFromLegalName(valueOrEvent);
    form.validateFields(["organizationName"]).catch(() => {});
  };

  const handleOrganizationDisplayNameChange = (valueOrEvent: unknown) => {
    form.setFieldsValue({
      organizationDisplayName: getInputValue(valueOrEvent),
    });
    form.validateFields(["organizationDisplayName"]).catch(() => {});
  };

  const handleValuesChange = (changed: Partial<OrganizationFormValues>) => {
    if (changed.removeOrganizationLogo === true) {
      form.setFieldsValue({ organizationLogoFile: null });
    }
    if (changed.organizationLogoFile) {
      form.setFieldsValue({ removeOrganizationLogo: false });
      setShowSavedLogoPreview(false);
    }
    updateFormValidity();
  };

  const trimmedExistingLogoUrl = existingLogoUrl?.trim() ?? "";

  return (
    <ErrorBoundary>
      <Form
        form={form}
        onFinish={onSubmit}
        onFieldsChange={updateFormValidity}
        onValuesChange={handleValuesChange}
        className="organizations-add-edit"
      >
        {mode === "edit" && (
          <Form.Item
            name="removeOrganizationLogo"
            valuePropName="isSelected"
            initialValue={false}
            className="hidden"
          >
            <Switch size="small" aria-hidden />
          </Form.Item>
        )}

        <Row>
          <Col span={6}>
            <Form.Item
              name="organizationName"
              label="Legal Name"
              validateTrigger={["onChange", "onBlur"]}
              rules={organizationNameRules}
            >
              <Input
                placeholder="Legal name"
                onChange={handleOrganizationNameChange}
                onInput={handleOrganizationNameChange}
                onKeyUp={handleOrganizationNameChange}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="organizationDisplayName"
              label="Display Name"
              validateTrigger={["onChange", "onBlur"]}
              rules={organizationDisplayNameRules}
            >
              <Input
                placeholder="Organization display name"
                onChange={handleOrganizationDisplayNameChange}
                onInput={handleOrganizationDisplayNameChange}
                onKeyUp={handleOrganizationDisplayNameChange}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={6}>
            <Form.Item
              name="organizationKey"
              label="Organization Key"
              validateTrigger={["onChange", "onBlur"]}
              rules={organizationKeyRules}
              helpText={
                "Once organization key is created, it cannot be changed."
              }
            >
              <Input
                placeholder="Organization key"
                isDisabled={organizationKeyDisabled}
                onChange={(valueOrEvent) => {
                  if (organizationKeyDisabled) return;
                  setIsKeyEditedManually(true);
                  form.setFieldsValue({
                    organizationKey: sanitizeKeyInput(
                      getInputValue(valueOrEvent),
                      ORGANIZATION_KEY_CHARS,
                    ),
                  });
                  form.validateFields(["organizationKey"]).catch(() => {});
                }}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item>
              <Flex
                align="start"
                gap={1}
                className="status-switch"
                direction="column"
              >
                <Paragraph className="m-0 custom-label">
                  Organization Status
                </Paragraph>
                <Form.Item
                  name="organizationIsActive"
                  valuePropName="isSelected"
                  noStyle
                >
                  <Switch
                    className="p-0"
                    size="small"
                    aria-label="Organization active status"
                  />
                </Form.Item>
              </Flex>
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={10}>
            <Form.Item
              name="organizationDescription"
              label="Organization Description"
              validateTrigger={["onChange", "onBlur"]}
              rules={DESCRIPTION_MAX_LENGTH_RULES}
            >
              <Textarea placeholder="Organization description" />
            </Form.Item>
          </Col>
          <Col span={2}>
            {mode === "edit" && trimmedExistingLogoUrl !== "" && (
              <SavedLogoPreview
                logoUrl={trimmedExistingLogoUrl}
                showPreview={showSavedLogoPreview}
                onRemove={() => {
                  form.setFieldsValue({
                    removeOrganizationLogo: true,
                    organizationLogoFile: null,
                  });
                  setShowSavedLogoPreview(false);
                  updateFormValidity();
                }}
              />
            )}
          </Col>
        </Row>

        <Form.Item
          name="organizationLogoFile"
          label="Organization Logo"
          validateTrigger={["onChange", "onBlur"]}
          rules={LOGO_FILE_VALIDATION_RULES}
        >
          <ImageCrop
            accept={LOGO_ACCEPT}
            aspect={1}
            outputSize={200}
            outputFileName="logo.jpg"
            onApply={() => {
              form.validateFields(["organizationLogoFile"]).catch(() => {});
            }}
          />
        </Form.Item>
      </Form>
    </ErrorBoundary>
  );
};
